import {
  ActionLogParams,
  useRGetActionLogToken,
} from '@repositories/action-log';
import { useRSubmitActionLog } from '@repositories/action-log/mutations';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import Cookies from 'js-cookie';
import COOKIES_KEY from 'constants/cookies';
import { jwtDecode } from 'jwt-decode';
import getUniqueId from '@utils/getUniqueId';
import { useProfile } from '..';

interface ActionLogProps {
  page?: string;
  view?: boolean;
  dropOff?: boolean;
}

const useActionLog = (props?: ActionLogProps) => {
  const { page = null, view = true, dropOff = true } = props || {};
  const { data: profile } = useProfile();

  const didMountRef = useRef<boolean | null>(null);
  const jwtToken = Cookies.get(COOKIES_KEY.JWT_TOKEN);
  const decodedToken = useMemo(
    () => (jwtToken ? jwtDecode(jwtToken) : null),
    [jwtToken],
  );
  if (jwtToken) delete decodedToken.expired_at;
  const uniqueId = getUniqueId();

  const applicationId = useMemo(
    () => profile?.application?.applicationId,
    [profile?.application?.applicationId],
  );

  useRGetActionLogToken({
    onSuccess: ({ data }) => {
      Cookies.set(COOKIES_KEY.JWT_TOKEN, data.jwtToken);
    },
  });

  const { mutate: submitLog } = useRSubmitActionLog();

  const handleSubmitLog = useCallback(
    (variables: ActionLogParams) => {
      if (decodedToken) {
        submitLog({
          variables: {
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ssXXX'),
            event: variables.event || 'action',
            module: page,
            ...decodedToken,
            application_id: applicationId || decodedToken.application_id,
            ...variables,
            attributes: {
              device_id: uniqueId,
              ...variables.attributes,
            },
          },
        });
      }
    },
    [decodedToken, page, applicationId, submitLog, uniqueId],
  );

  useEffect(() => {
    const handleBeforeUnload = () => {
      const navigationType = window.performance.getEntriesByType(
        'navigation',
      )[0] as PerformanceNavigationTiming;

      // Only trigger when the user is closing the tab or navigating to a new URL
      if (navigationType.type !== 'reload' && decodedToken) {
        submitLog({
          variables: {
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ssXXX'),
            module: page,
            element: page,
            event: 'drop_off',
            ...decodedToken,
            application_id: applicationId || decodedToken.application_id,
            attributes: {
              device_id: uniqueId,
            },
          },
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && decodedToken) {
        submitLog({
          variables: {
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ssXXX'),
            module: page,
            element: page,
            event: 'drop_off',
            ...decodedToken,
            application_id: applicationId || decodedToken.application_id,
            attributes: {
              device_id: uniqueId,
            },
          },
        });
      }
    };

    if (page && dropOff) {
      // Add the event listener when the component is mounted
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // Clean up the event listener when the component is unmounted
    return () => {
      if (page && dropOff) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      }
    };
  }, [decodedToken, page, dropOff, submitLog, uniqueId, applicationId]);

  useEffect(() => {
    if (!didMountRef.current && !!decodedToken && page && view) {
      didMountRef.current = true;

      submitLog({
        variables: {
          date: format(new Date(), 'yyyy-MM-dd HH:mm:ssXXX'),
          module: page,
          element: page,
          event: 'view',
          ...decodedToken,
          application_id: applicationId || decodedToken.application_id,
          attributes: {
            device_id: uniqueId,
          },
        },
      });
    }

    return () => {
      if (!!decodedToken && page && view) {
        submitLog({
          variables: {
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ssXXX'),
            module: page,
            element: page,
            event: 'change_page',
            ...decodedToken,
            application_id: applicationId || decodedToken.application_id,
            attributes: {
              device_id: uniqueId,
            },
          },
        });
      }
    };
  }, [decodedToken, page, view, submitLog, uniqueId, applicationId]);

  return {
    pushEvent: handleSubmitLog,
  };
};

export default useActionLog;
